import React, { useState, useEffect } from 'react';
import { Box } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import analytics from '../../../../modules/analytics';
import { getProfileKeyHighlights } from '../../../../modules/provider/profileKeyHighlights';
import type { ProSpecialty } from '../../../../modules/Api.types';
import { Accordion } from '../../../ui/Accordion/Accordion';
import { BodyText } from '../../../ui';
import { CircleCheckedIcon, VariousSparklesIcon } from '../../../ui/Icons';
import { BasicLightningIcon } from '../../../ui/Icons/BasicLightningIcon';
import { SecurityShieldOkIcon } from '../../../ui/Icons/SecurityShieldOkIcon';
import { PublicProvider } from '../../../../api/Providers';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';

const SPECIALTY_TRACKING_BASE = 'client_proprofile_about_specialty_';
const SPECIALTY_TITLE = 'Specializes in';

type ProProfileHighlightsProps = {
  provider: PublicProvider | ClientProviderProfile;
  specialties: ProSpecialty[];
  onExpandHighlight: ({ title: string, index: number }) => void;
  onCollapseHighlight: ({ title: string, index: number }) => void;
};

export const ProProfileHighlights: React.FCWithChildren<ProProfileHighlightsProps> = ({
  provider,
  specialties,
  onExpandHighlight,
  onCollapseHighlight,
}) => {
  const [expandedSections, setExpandedSections] = useState<number[]>([]);
  const handleAccordionChange = ({
    expanded, title, index,
  }) => {
    setExpandedSections(expanded);

    if (expanded.indexOf(index) !== -1 && title.includes(SPECIALTY_TITLE)) {
      analytics.track(`${SPECIALTY_TRACKING_BASE}click`);
    }
  };

  const highlights = getProfileKeyHighlights(provider);
  const sections = [
    {
      shouldDisplay: highlights.licensed,
      title: 'Licensed',
      icon: CircleCheckedIcon,
      iconColor: BrandColor.BackInBlack,
      text: 'Licensed professional. This is self reported.',
    },
    {
      shouldDisplay: highlights.oneThousandPlusBooked,
      title: '1K+ Clients Booked',
      icon: BasicLightningIcon,
      iconColor: BrandColor.Yellow,
      text: 'Over 1K clients booked on StyleSeat.',
    },
    {
      shouldDisplay: highlights.styleSeatVerified,
      title: 'StyleSeat Verified',
      icon: SecurityShieldOkIcon,
      iconColor: BrandColor.DarkBlue,
      text: 'Provided the required info to confirm their identity.',
    },
    {
      shouldDisplay: specialties.length > 0,
      title: `${SPECIALTY_TITLE} ${specialties.map(x => x.name).join(', ')}`,
      icon: VariousSparklesIcon,
      iconColor: BrandColor.DarkBlue,
      text: 'These specialties are self reported.',
    },
  ].filter(section => section.shouldDisplay);

  useEffect(() => {
    if (specialties?.length) {
      analytics.track(`${SPECIALTY_TRACKING_BASE}view`);
    }
  }, [specialties]);

  return (
    <Accordion
      expanded={expandedSections}
      onChange={handleAccordionChange}
      onSectionExpanded={onExpandHighlight}
      onSectionCollapsed={onCollapseHighlight}
    >
      {sections.map((section, idx) => (
        <Accordion.Section
          title={section.title}
          key={section.title}
          index={idx}
          icon={(
            <section.icon
              width={14}
              height={14}
              color={section.iconColor}
            />
          )}
        >
          <Accordion.Item>
            <Box
              paddingX={32}
              minHeight={48}
              testID="profile-highlights-explainer"
              textOverflow="ellipsis"
            >
              <BodyText appearance="medium" color={BrandColor.Metal}>{section.text}</BodyText>
            </Box>
          </Accordion.Item>
        </Accordion.Section>
      ))}
    </Accordion>
  );
};

export default ProProfileHighlights;
